import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const PropertyGallery = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  if (!images || images.length === 0) {
    return (
      <div className="w-full h-96 bg-muted flex items-center justify-center" data-testid="gallery-placeholder">
        <p className="text-muted-foreground">No images available</p>
      </div>
    );
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <>
      <div className="space-y-4" data-testid="property-gallery">
        <div className="relative overflow-hidden bg-black group cursor-pointer" onClick={() => setIsLightboxOpen(true)}>
          <img
            src={images[currentIndex].url}
            alt={images[currentIndex].description || `Property image ${currentIndex + 1}`}
            className="w-full h-[500px] object-cover"
            data-testid="gallery-main-image"
          />
          {images.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-primary w-12 h-12 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                data-testid="gallery-prev-button"
              >
                <ChevronLeft className="w-6 h-6" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-primary w-12 h-12 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                data-testid="gallery-next-button"
              >
                <ChevronRight className="w-6 h-6" />
              </Button>
            </>
          )}
          <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2">
            <p className="text-sm font-semibold">
              {currentIndex + 1} / {images.length}
            </p>
          </div>
        </div>

        {images.length > 1 && (
          <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`relative overflow-hidden aspect-square border-2 transition-all ${
                  index === currentIndex
                    ? 'border-accent'
                    : 'border-transparent hover:border-border'
                }`}
                data-testid={`gallery-thumbnail-${index}`}
              >
                <img
                  src={image.url}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {isLightboxOpen && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" data-testid="gallery-lightbox">
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/10 w-12 h-12"
            onClick={() => setIsLightboxOpen(false)}
            data-testid="lightbox-close-button"
          >
            <X className="w-6 h-6" />
          </Button>
          {images.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 w-12 h-12"
                onClick={goToPrevious}
                data-testid="lightbox-prev-button"
              >
                <ChevronLeft className="w-8 h-8" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 w-12 h-12"
                onClick={goToNext}
                data-testid="lightbox-next-button"
              >
                <ChevronRight className="w-8 h-8" />
              </Button>
            </>
          )}
          <img
            src={images[currentIndex].url}
            alt={images[currentIndex].description || `Property image ${currentIndex + 1}`}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
    </>
  );
};

export default PropertyGallery;